import {
  CloseCircleOutlined,
  EditOutlined,
  FileAddOutlined,
  FilterOutlined,
  InboxOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons'
import {
  Avatar,
  Button,
  Col,
  Comment,
  Input,
  Modal,
  Pagination,
  Row,
  Select,
  Switch,
  Tooltip,
  Upload,
  message,
} from 'antd'
import { inject, observer } from 'mobx-react'
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Helmet } from 'react-helmet/es/Helmet'
import { withRouter } from 'react-router-dom'
import AddUserForm from '../../components/AddUserForm'
import { EmptyText } from '../../components/Common/CellText'
import {
  CellEclipseBox,
  TableBottomPaginationBlock,
} from '../../components/Common/Table'
import TableComponent from '../../components/Common/TableComponent'
import EmptyContent from '../../components/EmptyContent'
import PageTitle from '../../components/PageTitle'
import UserAccountEditForm from '../../components/UserAccountEditForm'
import UserListFilterBlock from '../../components/UserListFilterBlock'
import DocumentsLayout from '../UserManagementPage/DocumentsLayout'
import ModalPreView from './ModalPreView'
import { FilterButton, SearchBar } from './UserAcountManagementPageStyled'
import {
  AddCircleOutlineIcon,
  FileSendOutlineIcon,
  MagnifierOutlineIcon,
  Pen2OutlineIcon,
} from '../../components/AppIcon'
import { AppTable } from '../../components/AppTable/AppTable'
import { AppTablePagination } from '../../components/AppTable/AppTablePagination'
import { blue } from '../../color'
import UserAccountManagementPageAdvanceFilterV2 from './UserAccountManagementPageAdvanceFilterV2'
import DraggerUploadButton from '../../components/Common/DraggerUploadButton'
import AppPageHeader from '../../components/AppPageHeader'

const { Search } = Input
const { Dragger } = Upload
const { Option } = Select

const UserAccountManagementPage = props => {
  const {
    userStore,
    loadingAnimationStore,
    companyStore,
    authenticationStore,
    history,
    positionStore,
    departmentStore,
    roleStore,
    fileStore,
  } = props

  const {
    userListPageIndex,
    userListPageSize,
    userListKeyword,
    userListStatus,
    userListDepartmentCode,
    userListSortDirection,
    userListSortBy,
  } = userStore

  const { companyList } = companyStore

  const [showUserInfo, setShowUserInfo] = useState(false)
  const [showAddUser, setShowAddUser] = useState(false)
  const [showFilter, setShowFilter] = useState(false)
  const [fileExcel, setFileExcel] = useState(null)
  const [importVisible, setImportVisible] = useState(false)
  const [dataExcel, setDataExcel] = useState(null)
  const [preViewVisible, setPreViewVisible] = useState(false)

  const tableColumns = useMemo(
    () => [
      {
        title: 'Họ tên',
        width: 240,
        key: 'fullname',
        render: record =>
          record?.name_lowercase ? (
            <span style={{ textTransform: 'capitalize' }}>
              {record?.name_lowercase}
            </span>
          ) : (
            <EmptyText>Không rõ.</EmptyText>
          ),
      },
      {
        title: 'ID',
        key: 'username',
        width: 150,
        render: record => record?.username,
      },
      {
        title: 'Mã nhân viên',
        key: 'ma_nv',
        width: 120,
        render: record =>
          record?.ma_nv ? record?.ma_nv : <EmptyText>Không rõ</EmptyText>,
      },
      {
        title: 'Email',
        key: 'email',
        width: 180,
        render: record => (
          <Tooltip title={record?.email}>
            <CellEclipseBox>{record?.email}</CellEclipseBox>
          </Tooltip>
        ),
      },
      {
        title: 'Số ĐT',
        key: 'phone',
        width: 150,
        render: record => <strong>{record?.phone}</strong>,
      },
      {
        title: 'Số máy nội bộ',
        key: 'home_phone',
        width: 150,
        render: record => (
          <strong>
            {record?.home_phone ? (
              record?.home_phone
            ) : (
              <EmptyText>Không rõ.</EmptyText>
            )}
          </strong>
        ),
      },
      {
        title: 'Trạng thái',
        key: 'status',
        width: 100,
        render: record => (
          <Switch
            onChange={status => handleUpdateUserStatus(record?.code, status)}
            checked={record?.status}
          />
        ),
      },
      {
        title: 'Công ty',
        key: 'companyName',
        width: 150,
        render: record => (
          <Tooltip title={record?.company && record?.company.name}>
            <CellEclipseBox>
              {record?.company && record?.company.name}
            </CellEclipseBox>
          </Tooltip>
        ),
      },
      {
        title: 'Tác vụ',
        key: 'action',
        fixed: 'right',
        align: 'center',
        width: 120,
        render: record => (
          <Button
            onClick={() => handleViewUserInfo(record)}
            type="text"
            shape="circle"
            icon={
              <Pen2OutlineIcon
                style={{
                  width: 18,
                  height: 18,
                  color: blue,
                }}
              />
            }
          />
        ),
      },
    ],
    []
  )

  const handleViewUserInfo = useCallback(
    userInfo => {
      const companyCode = userInfo?.company?.code
      userStore.setSelectedUser(userInfo)
      loadingAnimationStore.showSpinner(true)
      departmentStore
        .getDepartmentListFromCompanyCode(companyCode)
        .then(() => setShowUserInfo(true))
        .catch(error => message.error(error.vi))
        .finally(() => loadingAnimationStore.showSpinner(false))
    },
    [departmentStore, loadingAnimationStore, userStore]
  )

  const handleCancelViewUserInfo = useCallback(() => {
    userStore.clearSelectedUser()
    setShowUserInfo(false)
  }, [userStore])

  const handleChangePage = useCallback(
    (page, size) => {
      userStore.changeUserListPageIndex(page - 1)
      userStore.changeUserListPageSize(size || 10)
    },
    [userStore]
  )

  const handleShowAdvancedFilter = useCallback(
    status => {
      setShowFilter(status)
      if (!status) {
        userStore.clearUserListFilter()
      }
    },
    [userStore]
  )

  const handleUpdateUserStatus = useCallback(
    (userCode, status) => {
      loadingAnimationStore.showSpinner(true)
      userStore
        .updateUserStatus(userCode, status)
        .then(() => {
          return new Promise((resolve, reject) => {
            userStore
              .getUserList()
              .then(() => resolve())
              .catch(() => reject())
          })
        })
        .then(() => {
          message.success(`Đã ${status ? 'kích hoạt' : 'tạm dừng'} người dùng!`)
          loadingAnimationStore.showSpinner(false)
        })
        .catch(error => {
          message.error(error.vi)
          loadingAnimationStore.showSpinner(false)
        })
    },
    [loadingAnimationStore, userStore]
  )
  const handleCancelAddUser = useCallback(() => {
    setShowAddUser(false)
  }, [])

  useEffect(() => {
    loadingAnimationStore.showSpinner(true)
    Promise.all([
      companyStore.getCompanyList(),
      positionStore.getPositionList(userStore.companyCodeCurrent),
      roleStore.getRoles(),
    ])
      .then(() => loadingAnimationStore.showSpinner(false))
      .catch(() => loadingAnimationStore.showSpinner(false))
    return () => {
      userStore.clearUserListFilter()
      userStore.changeUserListKeyword(undefined)
      userStore.changeUserListPageIndex(0)
    }
  }, [
    companyStore,
    loadingAnimationStore,
    positionStore,
    roleStore,
    userStore,
    userStore.companyCodeCurrent,
  ])

  useEffect(() => {
    if (authenticationStore.currentUser) {
      loadingAnimationStore.showSpinner(true)
      // userStore.setFilter('userListHasAdmin', true)
      userStore.setFilter(
        'companyCode',
        userStore.companyCodeCurrent || authenticationStore.currentCompanyCode()
      )
      userStore
        .getUserList()
        .finally(() => loadingAnimationStore.showSpinner(false))
    }
  }, [
    userListPageIndex,
    userListPageSize,
    userListKeyword,
    userListStatus,
    userListDepartmentCode,
    userListSortDirection,
    userListSortBy,
    loadingAnimationStore,
    userStore,
    userStore.companyCodeCurrent,
    authenticationStore.currentUser,
  ])

  const handleGetDataExcel = async () => {
    if (!fileExcel) {
      message.error('Bạn chưa chọn file Excel.')
      return
    }
    const fileExtension = fileExcel.name.split('.').pop()

    if (fileExtension !== 'xlsx' && fileExtension !== 'xls') {
      message.error('Định dạng file không đúng.')
      return
    }
    loadingAnimationStore.showSpinner(true)
    const formData = new FormData()
    formData.append('file', fileExcel)
    try {
      const response = await fileStore.sendDataExcel(formData)
      setDataExcel(response)
      setPreViewVisible(true)
      loadingAnimationStore.showSpinner(false)
    } catch (error) {
      console.error('Error importing Excel:', error)
      message.error('Đã xảy ra lỗi khi import file Excel.')
      loadingAnimationStore.showSpinner(false)
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }
  useEffect(() => {
    if (fileExcel == null) return
    handleGetDataExcel()
  }, [fileExcel])

  useEffect(() => {
    if (!authenticationStore.currentUser) return
    // if (
    //   authenticationStore.currentUser &&
    //   authenticationStore.currentUser.roles.includes('SUPER_ADMIN')
    // )
    //   return
    // history.push('/')
    // message.error(
    //   'Bạn không có quyền truy cập trang này, vui lòng liên hệ admin'
    // )
  }, [authenticationStore.currentUser, history])

  useEffect(() => {
    return () => {
      userStore.clearStore()
    }
  }, [])

  const handleCancelImport = () => {
    setImportVisible(false)
    setFileExcel(null)
  }

  const handleRemoveFile = () => {
    setFileExcel(null)
  }

  const handleDownLoadTemplate = async () => {
    try {
      console.log('A1')
      loadingAnimationStore.showSpinner(true)
      const response = await fileStore.downloadTemplateAccount()
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'template.xlsx')
      document.body.appendChild(link)
      link.click()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading template:', error)
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }

  const onCompanyChange = value => {
    userStore.setFilter('companyCode', value)
    return () => {
      userStore.companyCode = null
    }
  }

  return (
    <>
      {!props.isTab ? (
        <DocumentsLayout
          title={
            <AppPageHeader
              title="Quản lý Tài khoản"
              addon={
                <>
                  <Search
                    allowClear
                    onSearch={userStore.changeUserListKeyword}
                    placeholder={'Tìm kiếm theo username'}
                    maxLength={500}
                    enterButton={
                      <MagnifierOutlineIcon
                        style={{ color: '#000', width: 16, height: 16 }}
                      />
                    }
                    className="custom-search-input fixed-width"
                  />

                  <UserAccountManagementPageAdvanceFilterV2 />

                  <Button
                    type="default"
                    className="custom-ant-button icon-btn"
                    onClick={() => setImportVisible(true)}
                    icon={
                      <FileSendOutlineIcon style={{ width: 16, height: 16 }} />
                    }>
                    Nhập file excel
                  </Button>

                  <Button
                    className="custom-ant-button icon-btn"
                    type={'primary'}
                    onClick={() => setShowAddUser(true)}
                    icon={
                      <AddCircleOutlineIcon
                        style={{ color: '#fff', width: 16, height: 16 }}
                      />
                    }>
                    Tạo mới người dùng
                  </Button>
                </>
              }
            />
          }
          selectedKey={'user-account-management'}>
          <Helmet>
            <title>Quản lý tài khoản</title>
          </Helmet>

          <AppTable
            rowKey={record => record?.code}
            dataSource={userStore.userHasAdminList}
            columns={tableColumns}
            pagination={false}
            // scroll={{ x: 1000 }}
            locale={{
              emptyText: <EmptyContent />,
            }}
          />

          <AppTablePagination
            onChange={handleChangePage}
            pageSize={userListPageSize}
            showLessItems
            current={userListPageIndex + 1}
            total={userStore.userListTotalCount}
            hideOnSinglePage={false}
          />
        </DocumentsLayout>
      ) : (
        <>
          <Row style={{ marginBottom: 16 }}>
            <Col span={12}>
              <SearchBar>
                <Search
                  allowClear
                  onSearch={userStore.changeUserListKeyword}
                  placeholder={'Tìm kiếm theo tên hoặc username'}
                />
              </SearchBar>
            </Col>
            <Col span={12} style={{ textAlign: 'right' }}>
              <FilterButton
                onClick={() => handleShowAdvancedFilter(!showFilter)}
                type={showFilter ? 'primary' : 'default'}
                ghost={showFilter}>
                {showFilter ? (
                  <Fragment>
                    <CloseCircleOutlined />
                    &nbsp; Tắt tìm kiếm nâng cao
                  </Fragment>
                ) : (
                  <Fragment>
                    <FilterOutlined />
                    &nbsp; Tìm kiếm nâng cao
                  </Fragment>
                )}
              </FilterButton>
            </Col>
          </Row>
          {showFilter ? <UserListFilterBlock /> : null}
          <TableComponent
            rowKey={record => record?.code}
            dataSource={userStore.userHasAdminList}
            columns={tableColumns}
            pagination={false}
            scroll={{ x: 1000 }}
            locale={{
              emptyText: <EmptyContent />,
            }}
          />
          <TableBottomPaginationBlock>
            <Pagination
              onChange={handleChangePage}
              pageSize={userListPageSize}
              showSizeChanger={false}
              showLessItems
              current={userListPageIndex + 1}
              total={userStore.userListTotalCount}
              hideOnSinglePage={true}
            />
          </TableBottomPaginationBlock>
        </>
      )}
      <UserAccountEditForm
        modalVisible={showUserInfo}
        onCancel={handleCancelViewUserInfo}
      />

      <AddUserForm modalVisible={showAddUser} onCancel={handleCancelAddUser} />
      <Modal
        wrapClassName="custom-modal-wrapper"
        title="Nhập file excel"
        visible={importVisible}
        onCancel={handleCancelImport}
        footer={null}>
        <Dragger
          className="custom-dragger-upload bg-white"
          fileList={fileExcel ? [fileExcel] : []}
          onRemove={handleRemoveFile}
          beforeUpload={file => {
            setFileExcel(file)
            return false
          }}
          multiple={false}>
          <DraggerUploadButton acceptFileLabel="XLS, XLSX" />
        </Dragger>

        <div className="custom-create-page-footer-sticky">
          <Button
            onClick={handleCancelImport}
            htmlType={'button'}
            type={'default'}
            className="custom-ant-button w-160">
            Hủy bỏ
          </Button>
          <Button
            onClick={handleDownLoadTemplate}
            type={'primary'}
            className="custom-ant-button w-160">
            Tải mẫu nhập
          </Button>
        </div>
      </Modal>
      {preViewVisible && (
        <ModalPreView
          data={dataExcel}
          setPreViewVisible={setPreViewVisible}
          onCancel={() => setPreViewVisible(false)}
        />
      )}
    </>
  )
}

export default withRouter(
  inject(
    'userStore',
    'loadingAnimationStore',
    'companyStore',
    'authenticationStore',
    'positionStore',
    'departmentStore',
    'roleStore',
    'fileStore'
  )(observer(UserAccountManagementPage))
)
